import { useEffect } from 'react';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import {
  useSuiteAPI,
  UseSuiteAPIReturn,
} from 'src/SharedCustomHooks/UseSuiteAPI/useSuiteAPI';
import { SidebarContextRow } from '../Components/SidebarContextTree';
import {
  assetRow,
  keyPickupRow,
  parseSuiteRouteId,
  suiteRow,
} from './sidebarContextRows.helpers';
import {
  recallPropertyName,
  rememberPropertyName,
} from './strataPropertyNameCache';

interface RouteParams {
  id: string;
}

interface RouteMatch<Params> {
  path: string;
  url: string;
  isExact: boolean;
  params: Params;
}

interface SuiteRouteState {
  // passed by the Manage Suites landing's "Go to unit" nav (see StrataManagerUnitCard)
  propertyName?: string;
  // passed by the shared SR table when opening a request: the suite path + its label
  navigateTo?: string;
  navLabel?: string;
  // asset drill-down (mirrors usePropertyContextRows)
  asset?: any;
  suite?: any;
  property?: any;
}

// Unlike the admin breadcrumb, the property row links to the Strata Manager landing
// (there's no property-detail route for this role), not PROPERTIES_VIEW.
const propertyRow = (propertyName: string | null): SidebarContextRow => ({
  label: propertyName ?? '',
  path: PATHS.PROPERTIES_LIST,
  icon: 'building',
  loading: !propertyName,
});

const serviceRequestRow = (
  serviceRequestRouteId: string,
  label: string | null
): SidebarContextRow => ({
  label: label ?? 'Service request',
  path: PATHS.SERVICE_REQUESTS_VIEW.replace(':id', serviceRequestRouteId),
  icon: 'wrench',
  depth: 2,
});

const buildAssetRows = (
  assetRouteId: string,
  state: SuiteRouteState | undefined
): SidebarContextRow[] | null => {
  if (!state?.asset || !state?.suite || !state?.property) {
    return null;
  }

  const { asset, suite, property }: SuiteRouteState = state;

  return [
    propertyRow(property.name ?? null),
    suiteRow(`${property.id}&${suite.id}`, suite.number ?? null),
    assetRow(assetRouteId, asset.item?.name ?? null),
  ];
};

/* The shared SR table forwards { navigateTo, navLabel } (the suite path + label) when a
 * request is opened, but not the property name — so we recall it from the cache by the
 * suiteId parsed out of navigateTo to restore the full Property → Suite → SR breadcrumb.
 * If it's a cache miss, we degrade gracefully to Suite → SR. */
const buildServiceRequestRows = (
  serviceRequestRouteId: string,
  state: SuiteRouteState | undefined
): SidebarContextRow[] | null => {
  if (!state?.navigateTo) {
    return null;
  }

  const suiteComposite: string = state.navigateTo.split('/').pop() ?? '';
  const { suiteId } = parseSuiteRouteId(suiteComposite);
  const propertyName: string | null = recallPropertyName(suiteId);

  const rows: SidebarContextRow[] = [];

  if (propertyName) {
    rows.push(propertyRow(propertyName));
  }

  rows.push(suiteRow(suiteComposite, state.navLabel ?? null));
  rows.push(serviceRequestRow(serviceRequestRouteId, null));

  return rows;
};

const usePropertyContextRowsStrataManager = (): SidebarContextRow[] | null => {
  const assetRouteMatch: RouteMatch<RouteParams> | null =
    useRouteMatch<RouteParams>({ path: PATHS.ASSETS_VIEW });

  const keyPickupRouteMatch: RouteMatch<RouteParams> | null =
    useRouteMatch<RouteParams>({ path: PATHS.KEY_PICKUP_VIEW });

  const serviceRequestRouteMatch: RouteMatch<RouteParams> | null =
    useRouteMatch<RouteParams>({ path: PATHS.SERVICE_REQUESTS_VIEW });

  const suiteRouteMatch: RouteMatch<RouteParams> | null =
    useRouteMatch<RouteParams>({ path: PATHS.SUITES_VIEW });

  const location = useLocation<SuiteRouteState>();

  const rawSuiteId: string =
    suiteRouteMatch?.params?.id || keyPickupRouteMatch?.params?.id || '';

  const { suiteId } = parseSuiteRouteId(rawSuiteId);

  const navPropertyName: string | null = location.state?.propertyName ?? null;

  const { suite, fetchSuiteById }: UseSuiteAPIReturn = useSuiteAPI();

  useEffect(() => {
    if (!suiteId) {
      return;
    }

    fetchSuiteById(suiteId);
  }, [suiteId]);

  /* Cache the property name whenever the landing hands it to us, so it survives the
   * SR round-trip (see strataPropertyNameCache for the why). */
  useEffect(() => {
    if (suiteId && navPropertyName) {
      rememberPropertyName(suiteId, navPropertyName);
    }
  }, [suiteId, navPropertyName]);

  /* legacy/workaround: suite payloads don't carry the property name, so resolve it from
   * the landing nav state, then the suite payload, then the session cache (back-nav). */
  const propertyName: string | null =
    navPropertyName || suite?.propertyName || recallPropertyName(suiteId);

  const propertyContextRow: SidebarContextRow = propertyRow(propertyName);

  const suiteContextRow: SidebarContextRow = suiteRow(
    rawSuiteId,
    suite?.number || null
  );

  if (assetRouteMatch) {
    return buildAssetRows(assetRouteMatch.params.id, location.state);
  }

  if (serviceRequestRouteMatch) {
    return buildServiceRequestRows(
      serviceRequestRouteMatch.params.id,
      location.state
    );
  }

  if (keyPickupRouteMatch) {
    return [propertyContextRow, suiteContextRow, keyPickupRow(rawSuiteId)];
  }

  if (suiteRouteMatch) {
    return [propertyContextRow, suiteContextRow];
  }

  return null;
};

export default usePropertyContextRowsStrataManager;